import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Chip,
    Checkbox,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Avatar,
    TableContainer,
    Paper,
    Box,
    IconButton,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import ConfirmationDialog from './ConfirmationDialog.comp';
import { LEARNING_PROGRAM_STATUS } from '../../../utils/constants';

const ORANGE = 'var(--brand-primary, #FF6900)';

const normCode = (c) => (c == null ? '' : String(c));

const pickerCheckboxSx = {
    p: 0.5,
    color: '#D1D5DB',
    '&.Mui-checked': {
        color: ORANGE,
    },
    '& .MuiSvgIcon-root': { fontSize: 22 },
};

/** Learning program picker — Manage Classes (class-level linking). */
const ManageClassesLinkProgramDialog = ({
    programs,
    open,
    onSubmit,
    loading,
    onClose,
    orgName,
    orgCode,
    linkedProgramCodes = [],
    orgWideLinkedCodes = [],
    sectionLinkedCodes = [],
    linkingLoading = false,
    forSectionName,
    forSectionCode,
}) => {
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
    const [showSecondConfirmation, setShowSecondConfirmation] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedCodes([]);
            setShowFirstConfirmation(false);
            setShowSecondConfirmation(false);
        }
    }, [open]);

    const linkedSet = new Set((linkedProgramCodes || []).map(normCode));
    const orgWideSet = new Set((orgWideLinkedCodes || []).map(normCode));
    const sectionLinkedSet = new Set((sectionLinkedCodes || []).map(normCode));

    const handleToggle = (code) => {
        const key = normCode(code);
        setSelectedCodes((prev) => {
            const prevNorm = prev.map(normCode);
            return prevNorm.includes(key) ? prev.filter((c) => normCode(c) !== key) : [...prev, code];
        });
    };

    const handleSubmit = () => setShowFirstConfirmation(true);
    const handleFirstConfirm = () => {
        setShowFirstConfirmation(false);
        setShowSecondConfirmation(true);
    };
    const handleSecondConfirm = () => {
        setShowSecondConfirmation(false);
        onSubmit(orgCode, selectedCodes);
    };

    const linked = programs.filter((a) => linkedSet.has(normCode(a.code)));
    const unlinked = programs.filter((a) => !linkedSet.has(normCode(a.code)));
    const isClassContext = Boolean(forSectionName || forSectionCode);
    const pickerHint = isClassContext
        ? 'Select the learning program(s) that you would like to link to this class.'
        : 'Select the learning program(s) that you would like to link to this client.';
    // Class name only — the class code is not shown (Ian-PW,SNW,SW-19Oct-1).
    const classLabel = forSectionName || forSectionCode || '';
    const linkTargetLabel = isClassContext ? (classLabel || 'this class') : orgName;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    maxHeight: 'min(92vh, 860px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    backgroundColor: 'var(--brand-surface, #fff)',
                    backgroundImage: 'none',
                },
            }}
            BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } }}
        >
            <IconButton
                aria-label="Close dialog"
                onClick={onClose}
                size="small"
                disabled={loading}
                sx={{ position: 'absolute', top: 14, right: 14, color: 'var(--brand-text-muted, #9CA3AF)', '&:hover': { color: 'var(--brand-text, #111)' } }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
            {/* Same layout as My Schools' Link Learning Programs (LinkProgramDialog) —
                Ian-PW,SNW,SW-19Oct-1: every link pop-up looks the same. */}
            <DialogTitle sx={{ color: '#111', fontSize: '22px', fontWeight: 800, textAlign: 'center', pt: 4, pb: 0, flexShrink: 0 }}>
                Link Learning Programs
            </DialogTitle>
            {(isClassContext ? classLabel : orgName) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1, flexShrink: 0 }}>
                    <Box sx={{
                        display: 'inline-block', backgroundColor: 'rgba(255, 105, 0, 0.08)', color: '#F15A24',
                        fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '20px',
                    }}>
                        {isClassContext ? classLabel : orgName}
                    </Box>
                </Box>
            ) : null}

            {/* ── Content ── */}
            <DialogContent sx={{ px: 4, flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
                {linkingLoading ? (
                    <Typography>Loading programs...</Typography>
                ) : (
                    <>
                    <Typography sx={{ textAlign: 'center', fontSize: '15px', color: '#666', mb: 3 }}>
                        {pickerHint}
                    </Typography>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', borderRadius: '16px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Program</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Time (mins)</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Select</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...linked, ...unlinked].map((program) => {
                                    const rowCode = normCode(program.code);
                                    const alreadyLinked = linkedSet.has(rowCode);
                                    const isOrgWide = orgWideSet.has(rowCode);
                                    const isSectionLinked = sectionLinkedSet.has(rowCode) && !isOrgWide;
                                    const notReady = !program.is_ready;
                                    const notPublished = !alreadyLinked && !notReady && Number(program.status) !== LEARNING_PROGRAM_STATUS.PUBLISHED;
                                    const disabledCheckTitle = isOrgWide
                                        ? 'Already linked org-wide'
                                        : 'Already linked to this class';
                                    return (
                                        <TableRow
                                            key={program.code || program.id}
                                            sx={{
                                                backgroundColor: alreadyLinked ? 'rgba(255,105,0,0.06)' : 'inherit',
                                                '&:last-child td': { borderBottom: 'none' },
                                                '&:hover': { backgroundColor: alreadyLinked ? 'rgba(255,105,0,0.06)' : 'var(--brand-bg, #FAFAFA)' },
                                            }}
                                        >
                                            <TableCell sx={{ borderBottom: '1px solid var(--brand-border, #F3F4F6)', py: 1.75 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar
                                                        src={program.image_url}
                                                        variant="rounded"
                                                        sx={{ width: 44, height: 44, borderRadius: '10px', border: '1px solid var(--brand-border, #F3F4F6)' }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: 'var(--brand-text, #111827)', lineHeight: 1.4 }}>
                                                            {program.full_name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.4, flexWrap: 'wrap' }}>
                                                            {isOrgWide && (
                                                                <Chip label="Org-wide" size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(0,151,178,0.1)', color: '#007a8c' }} />
                                                            )}
                                                            {isSectionLinked && (
                                                                <Chip label="Linked" size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(255,105,0,0.1)', color: ORANGE }} />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: '1px solid var(--brand-border, #F3F4F6)', color: 'var(--brand-text-muted, #6B7280)', fontSize: '14px' }}>
                                                {String(program.time_to_complete_minutes ?? '').replace(/\s*min$/i, '').trim() || '—'}
                                            </TableCell>
                                            <TableCell align="center" sx={{ borderBottom: '1px solid #F3F4F6' }}>
                                                {alreadyLinked ? (
                                                    <Chip
                                                        label="Linked"
                                                        size="small"
                                                        title={disabledCheckTitle}
                                                        sx={{ height: 22, fontSize: '11px', fontWeight: 700, backgroundColor: 'rgba(0,151,178,0.08)', color: '#007a8c' }}
                                                    />
                                                ) : notReady ? (
                                                    <Chip
                                                        label="Not Ready"
                                                        size="small"
                                                        sx={{ height: 22, fontSize: '11px', fontWeight: 700, backgroundColor: 'rgba(255,105,0,0.08)', color: ORANGE }}
                                                    />
                                                ) : notPublished ? (
                                                    <Chip
                                                        label="Not Published"
                                                        size="small"
                                                        title="Publish the program before linking it to a section."
                                                        sx={{ height: 22, fontSize: '11px', fontWeight: 700, backgroundColor: 'rgba(158,123,0,0.1)', color: '#9E7B00' }}
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        checked={selectedCodes.some((c) => normCode(c) === rowCode)}
                                                        onChange={() => handleToggle(program.code)}
                                                        sx={pickerCheckboxSx}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ pt: 3, pb: 4, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            minWidth: 180, px: 4, py: 1.25, borderRadius: '10px',
                            backgroundColor: '#fff', color: '#111', fontWeight: 700, fontSize: '16px', textTransform: 'none',
                            border: '1.5px solid #111',
                            '&:hover': { backgroundColor: '#f7f7f7', borderColor: '#111' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || selectedCodes.length === 0}
                        sx={{
                            minWidth: 180, px: 4, py: 1.25, borderRadius: '10px',
                            backgroundColor: '#FF6900', color: '#fff', fontWeight: 700, fontSize: '16px', textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                            '&.Mui-disabled': { backgroundColor: '#FFD0AA', color: '#fff' },
                        }}
                    >
                        {loading ? 'Linking...' : `Link (${selectedCodes.length})`}
                    </Button>
                </Box>
            </DialogActions>

            <ConfirmationDialog
                open={showFirstConfirmation}
                onClose={() => setShowFirstConfirmation(false)}
                onConfirm={handleFirstConfirm}
                title="Confirm linking"
                message={`Do you want to link <b>${selectedCodes.length}</b> program(s) to <b>${linkTargetLabel}</b>?`}
                confirmText="Proceed"
                cancelText="Cancel"
            />
            <ConfirmationDialog
                open={showSecondConfirmation}
                onClose={() => setShowSecondConfirmation(false)}
                onConfirm={handleSecondConfirm}
                title="Are You Sure?"
                message="Are you sure? Unlinking will not be possible once linked."
                confirmText="Yes, Link Now"
                cancelText="Cancel"
                isWarning={true}
            />
        </Dialog>
    );
};

export default ManageClassesLinkProgramDialog;
